import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import React from 'react';

import { MoreActionsIcon } from '../../../icons';
import { IconButton } from '../../atoms/IconButton';
import {
  type ResourceMenuAction,
  type ResourceMenuActionsProps,
  type ResourceMenuDivider,
  type ResourceMenuItem,
  useResourceMenuActions,
} from './useResourceMenuActions';

type Props = ResourceMenuActionsProps;

export function ResourceMenuActions(props: Props) {
  const hook = useResourceMenuActions(props);

  if (!hook.hasItems) {
    return null;
  }

  return (
    <>
      <IconButton
        size={hook.size}
        tooltip={hook.tooltip}
        aria-label={hook.ariaLabel}
        id={hook.menuButtonId}
        aria-controls={hook.isMenuOpen ? hook.menuId : undefined}
        aria-expanded={hook.isMenuOpen ? 'true' : undefined}
        aria-haspopup="true"
        disabled={props.disabled}
        onClick={hook.handleMenuOpen}
        data-testid={props['data-testid']}
      >
        <MoreActionsIcon fontSize={hook.size === 'large' ? 'medium' : 'small'} />
      </IconButton>

      <Menu
        id={hook.menuId}
        anchorEl={hook.anchorEl}
        open={hook.isMenuOpen}
        onClose={hook.handleMenuClose}
        slotProps={{
          list: {
            'aria-labelledby': hook.menuButtonId,
          },
          paper: {
            style: {
              maxHeight: hook.itemHeight * 4.5,
              minWidth: '20ch',
            },
          },
        }}
      >
        {hook.items.map((item, index) => {
          if ('type' in item && item.type === 'divider') {
            return <Divider key={item.id ?? `divider-${index}`} component="li" sx={{ my: 0.5 }} />;
          }

          const action = item;
          const actionKey = action.id ?? index;

          return (
            <React.Fragment key={actionKey}>
              <MenuItem
                onClick={(event) => hook.handleActionClick(action.onClick, event)}
                disabled={action.disabled}
                data-testid={action['data-testid']}
              >
                {action.icon ? (
                  <ListItemIcon>
                    {action.icon}
                  </ListItemIcon>
                ) : null}
                <ListItemText>{action.label}</ListItemText>
              </MenuItem>
              {action.divider ? <Divider component="li" sx={{ my: 0.5 }} /> : null}
            </React.Fragment>
          );
        })}
      </Menu>
    </>
  );
}

export type {
  ResourceMenuAction,
  ResourceMenuActionsProps,
  ResourceMenuDivider,
  ResourceMenuItem,
};
